import { FunctionComponent, useCallback, useEffect, useState } from "react";

import Button from "@ui/components/cta/Button/Button";
import CloseIcon from "@ui/components/icon/CloseIcon";
import theme from "@ui/themes/theme-contract.css";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";

import * as styles from "./LoginMessage.css";

interface LoginMessageProps {
  Logo?: FunctionComponent<{ className?: string }>;
  onCloseNotifications?: () => void;
  showCloseButton?: boolean;
}

const LoginMessage: FunctionComponent<LoginMessageProps> = ({
  Logo,
  onCloseNotifications,
  showCloseButton = true,
}) => {
  const { communityConfig } = useConfigContext();
  const analytics = useAnalytics();

  const [authLink, setAuthLink] = useState("/auth/login?returnPath=/");
  useEffect(() => {
    const { ssoAuthDomain, isPrimaryCommunity } = communityConfig;
    const { protocol, href, pathname } = window.location;

    let authLink: string;

    // If this community is part of an SSO network but not the primary community,
    // redirect to the SSO auth host for login with a returnTo param back to the current URL.
    if (ssoAuthDomain && !isPrimaryCommunity) {
      authLink = `${protocol}//${ssoAuthDomain}/auth/sso?returnTo=${encodeURIComponent(href)}`;
    } else {
      // Either this community is the primary community or it is not part of an SSO network
      // so proceed with the normal login/signup flow.
      authLink = `/auth/login?returnPath=${encodeURIComponent(pathname)}`;
    }

    setAuthLink(authLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickSignIn = useCallback(() => {
    analytics?.emit({
      event: "authentication",
      authentication: {
        method: "notification_top_navigation",
      },
    });
  }, [analytics]);

  return (
    <div className={styles.loginContainer}>
      {showCloseButton && (
        <Button
          className={styles.closeButton}
          text=""
          iconLeft={<CloseIcon fill={theme.sys.color.subtle} />}
          displayType="cycloneSecondary"
          onClick={onCloseNotifications}
        />
      )}
      <div className={styles.messageContainer}>
        {Logo && <Logo className={styles.logo} />}
        <p className={styles.message}>
          Sign in to see your notifications or create an account to join the
          conversation.
        </p>
        <Button text="Sign in" href={authLink} onClick={onClickSignIn} />
      </div>
    </div>
  );
};

export default LoginMessage;
